import { motion } from 'framer-motion';
import { memo, useMemo } from 'react';

type EnvironmentVariant = 'snow' | 'rain' | 'sunny' | 'space' | 'jungle' | 'ocean' | 'arctic' | 'hotboxed';

const createSequence = (count: number) => Array.from({ length: count }, (_, index) => index);

const pseudoRandom = (seed: number) => {
  const value = Math.sin(seed * 9301 + 49297) * 233280;
  return value - Math.floor(value);
};

type Snowflake = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  drift: number;
};

const SnowEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const snowflakes = useMemo(
    () =>
      createSequence(40).map<Snowflake>((id) => {
        const left = pseudoRandom(id) * 100;
        const delay = pseudoRandom(id + 1) * 3;
        const duration = 3.5 + pseudoRandom(id + 2) * 3;
        const size = 1.5 + pseudoRandom(id + 3) * 3;
        const drift = pseudoRandom(id + 4) * 30 - 15;
        return { id, left, delay, duration, size, drift };
      }),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      {snowflakes.map((flake) => (
        <motion.div
          key={flake.id}
          className='absolute rounded-full bg-white shadow-sm'
          style={{
            left: `${flake.left}%`,
            width: `${flake.size}px`,
            height: `${flake.size}px`,
            opacity: 0.6 + pseudoRandom(flake.id + 5) * 0.4,
            filter: 'blur(0.5px)',
            boxShadow: '0 0 4px rgba(255, 255, 255, 0.3)'
          }}
          initial={{ y: '-20px', x: 0 }}
          animate={shouldAnimate ? {
            y: 'calc(100vh + 20px)',
            x: [0, flake.drift * 0.5, flake.drift, flake.drift * 0.5, 0],
            rotate: [0, 360]
          } : { y: '50vh', x: 0, rotate: 0 }}
          transition={shouldAnimate ? {
            duration: flake.duration,
            repeat: Infinity,
            ease: 'linear',
            delay: flake.delay,
            rotate: {
              duration: flake.duration * 0.7,
              repeat: Infinity,
              ease: 'linear'
            }
          } : {}}
        />
      ))}
    </div>
  );
});

SnowEffect.displayName = 'SnowEffect';

type Raindrop = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  opacity: number;
};

const RainEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const raindrops = useMemo(
    () =>
      createSequence(35).map<Raindrop>((id) => {
        const left = pseudoRandom(id + 100) * 100;
        const delay = pseudoRandom(id + 101) * 2;
        const duration = 0.3 + pseudoRandom(id + 102) * 0.5;
        const opacity = 0.4 + pseudoRandom(id + 103) * 0.5;
        return { id, left, delay, duration, opacity };
      }),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      {raindrops.map((drop) => (
        <motion.div
          key={drop.id}
          className='absolute bg-gradient-to-b from-blue-400/80 via-blue-500/60 to-transparent'
          style={{
            left: `${drop.left}%`,
            width: '1.5px',
            height: `${60 + pseudoRandom(drop.id + 104) * 40}px`,
            opacity: drop.opacity,
            filter: 'blur(0.3px)',
            boxShadow: '0 0 2px rgba(59, 130, 246, 0.3)',
            transform: 'skewX(-8deg)'
          }}
          initial={{ y: '-80px' }}
          animate={shouldAnimate ? { y: 'calc(100vh + 80px)' } : { y: '40vh' }}
          transition={shouldAnimate ? {
            duration: drop.duration,
            repeat: Infinity,
            ease: 'linear',
            delay: drop.delay
          } : {}}
        />
      ))}
      <motion.div
        className='absolute inset-0 bg-gradient-to-b from-blue-900/5 via-blue-800/3 to-transparent'
        initial={{ opacity: 0.3 }}
        animate={shouldAnimate ? { opacity: [0.2, 0.4, 0.2] } : { opacity: 0.3 }}
        transition={shouldAnimate ? { duration: 3, repeat: Infinity, ease: 'easeInOut' } : {}}
      />
    </div>
  );
});

RainEffect.displayName = 'RainEffect';

type Sunbeam = {
  id: number;
  rotation: number;
  delay: number;
  duration: number;
  scale: number;
};

const SunnyEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const sunbeams = useMemo(
    () =>
      createSequence(12).map<Sunbeam>((id) => {
        const delay = pseudoRandom(id + 200) * 2.5;
        const duration = 4 + pseudoRandom(id + 201) * 3;
        const scale = 0.7 + pseudoRandom(id + 202) * 0.6;
        return { id, rotation: id * 30, delay, duration, scale };
      }),
    []
  );

  const sparkles = useMemo(
    () =>
      createSequence(15).map((id) => ({
        id: id + 1000,
        left: 20 + pseudoRandom(id + 300) * 60,
        top: 20 + pseudoRandom(id + 301) * 60,
        delay: pseudoRandom(id + 302) * 4,
        duration: 2 + pseudoRandom(id + 303) * 2,
        size: 2 + pseudoRandom(id + 304) * 3
      })),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      <motion.div
        className='absolute inset-[-40%] rounded-full bg-gradient-to-br from-yellow-400/6 via-orange-300/4 to-amber-200/2'
        initial={{ rotate: 0, scale: 0.8 }}
        animate={shouldAnimate ? {
          rotate: 360,
          scale: [0.8, 0.9, 0.8]
        } : { rotate: 0, scale: 0.8 }}
        transition={shouldAnimate ? {
          rotate: { repeat: Infinity, duration: 60, ease: 'linear' },
          scale: { repeat: Infinity, duration: 8, ease: 'easeInOut' }
        } : {}}
      />

      {sunbeams.map((beam) => (
        <motion.div
          key={beam.id}
          className='absolute left-1/2 top-1/2 h-48 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-yellow-300/25 via-yellow-200/15 to-transparent'
          style={{
            rotate: `${beam.rotation}deg`,
            filter: 'blur(1px)',
            boxShadow: '0 0 8px rgba(255, 193, 7, 0.3)'
          }}
          initial={{ opacity: 0.2, scale: beam.scale }}
          animate={shouldAnimate ? {
            opacity: [0.1, 0.4, 0.1],
            scale: [beam.scale, beam.scale + 0.15, beam.scale],
            rotate: [beam.rotation, beam.rotation + 10, beam.rotation]
          } : { opacity: 0.2, scale: beam.scale, rotate: beam.rotation }}
          transition={shouldAnimate ? {
            repeat: Infinity,
            duration: beam.duration,
            ease: 'easeInOut',
            delay: beam.delay
          } : {}}
        />
      ))}

      <motion.div
        className='absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br from-yellow-300/40 via-yellow-200/25 to-amber-100/15'
        style={{
          filter: 'blur(2px)',
          boxShadow: '0 0 20px rgba(255, 193, 7, 0.4), inset 0 0 20px rgba(255, 235, 59, 0.2)'
        }}
        initial={{ scale: 0.85, opacity: 0.5 }}
        animate={shouldAnimate ? {
          scale: [0.85, 1.1, 0.85],
          opacity: [0.4, 0.7, 0.4]
        } : { scale: 0.85, opacity: 0.5 }}
        transition={shouldAnimate ? { repeat: Infinity, duration: 6, ease: 'easeInOut' } : {}}
      />

      {sparkles.map((sparkle) => (
        <motion.div
          key={sparkle.id}
          className='absolute rounded-full bg-yellow-200'
          style={{
            left: `${sparkle.left}%`,
            top: `${sparkle.top}%`,
            width: `${sparkle.size}px`,
            height: `${sparkle.size}px`,
            filter: 'blur(0.5px)',
            boxShadow: '0 0 4px rgba(255, 235, 59, 0.6)'
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={shouldAnimate ? {
            opacity: [0, 0.8, 0],
            scale: [0, 1.2, 0],
            rotate: [0, 180, 360]
          } : { opacity: 0.4, scale: 0.8, rotate: 0 }}
          transition={shouldAnimate ? {
            repeat: Infinity,
            duration: sparkle.duration,
            ease: 'easeInOut',
            delay: sparkle.delay
          } : {}}
        />
      ))}
    </div>
  );
});

SunnyEffect.displayName = 'SunnyEffect';

type Star = {
  id: number;
  left: number;
  top: number;
  delay: number;
  duration: number;
  size: number;
  brightness: number;
};

const SpaceEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const stars = useMemo(
    () =>
      createSequence(50).map<Star>((id) => {
        const left = pseudoRandom(id + 400) * 100;
        const top = pseudoRandom(id + 401) * 100;
        const delay = pseudoRandom(id + 402) * 5;
        const duration = 2 + pseudoRandom(id + 403) * 3;
        const size = 1 + pseudoRandom(id + 404) * 2.5;
        const brightness = 0.3 + pseudoRandom(id + 405) * 0.7;
        return { id, left, top, delay, duration, size, brightness };
      }),
    []
  );

  const floatingParticles = useMemo(
    () =>
      createSequence(20).map((id) => ({
        id: id + 2000,
        left: pseudoRandom(id + 500) * 100,
        delay: pseudoRandom(id + 501) * 4,
        duration: 8 + pseudoRandom(id + 502) * 6,
        size: 2 + pseudoRandom(id + 503) * 4,
        drift: pseudoRandom(id + 504) * 60 - 30
      })),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      <motion.div
        className='absolute inset-0 bg-gradient-to-b from-purple-900/20 via-indigo-900/15 to-black/10'
        initial={{ opacity: 0.6 }}
        animate={shouldAnimate ? { opacity: [0.5, 0.8, 0.5] } : { opacity: 0.6 }}
        transition={shouldAnimate ? { duration: 8, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      {stars.map((star) => (
        <motion.div
          key={star.id}
          className='absolute rounded-full bg-white'
          style={{
            left: `${star.left}%`,
            top: `${star.top}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
            opacity: star.brightness,
            filter: 'blur(0.2px)',
            boxShadow: `0 0 ${star.size * 2}px rgba(255, 255, 255, ${star.brightness})`
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={shouldAnimate ? {
            opacity: [0, star.brightness, 0],
            scale: [0, 1.2, 0.8, 1],
            rotate: [0, 360]
          } : { opacity: star.brightness * 0.6, scale: 0.8, rotate: 0 }}
          transition={shouldAnimate ? {
            duration: star.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: star.delay
          } : {}}
        />
      ))}
    </div>
  );
});

SpaceEffect.displayName = 'SpaceEffect';

type Leaf = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  rotation: number;
  drift: number;
  color: string;
};

const JungleEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const leaves = useMemo(
    () =>
      createSequence(25).map<Leaf>((id) => {
        const left = pseudoRandom(id + 600) * 100;
        const delay = pseudoRandom(id + 601) * 4;
        const duration = 4 + pseudoRandom(id + 602) * 4;
        const size = 8 + pseudoRandom(id + 603) * 12;
        const rotation = pseudoRandom(id + 604) * 360;
        const drift = pseudoRandom(id + 605) * 50 - 25;
        const colors = ['#22c55e', '#16a34a', '#15803d', '#65a30d', '#84cc16'];
        const color = colors[Math.floor(pseudoRandom(id + 606) * colors.length)];
        return { id, left, delay, duration, size, rotation, drift, color };
      }),
    []
  );

  const fireflies = useMemo(
    () =>
      createSequence(12).map((id) => ({
        id: id + 3000,
        left: 10 + pseudoRandom(id + 700) * 80,
        top: 20 + pseudoRandom(id + 701) * 60,
        delay: pseudoRandom(id + 702) * 6,
        duration: 3 + pseudoRandom(id + 703) * 2,
        moveRange: 20 + pseudoRandom(id + 704) * 30
      })),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      <motion.div
        className='absolute inset-0 bg-gradient-to-b from-green-900/15 via-emerald-800/10 to-green-700/5'
        initial={{ opacity: 0.7 }}
        animate={shouldAnimate ? { opacity: [0.6, 0.9, 0.6] } : { opacity: 0.7 }}
        transition={shouldAnimate ? { duration: 12, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      {leaves.map((leaf) => (
        <motion.div
          key={leaf.id}
          className='absolute rounded-full'
          style={{
            left: `${leaf.left}%`,
            width: `${leaf.size}px`,
            height: `${leaf.size * 0.6}px`,
            backgroundColor: leaf.color,
            opacity: 0.7,
            filter: 'blur(0.3px)',
            borderRadius: '50% 10% 50% 10%',
            boxShadow: `0 0 4px ${leaf.color}40`
          }}
          initial={{ y: '-20px', rotate: leaf.rotation }}
          animate={shouldAnimate ? {
            y: 'calc(100vh + 20px)',
            x: [0, leaf.drift * 0.3, leaf.drift, leaf.drift * 0.7, 0],
            rotate: [leaf.rotation, leaf.rotation + 180, leaf.rotation + 360],
            scale: [1, 0.8, 1.1, 0.9, 1]
          } : { y: '50vh', x: 0, rotate: leaf.rotation, scale: 1 }}
          transition={shouldAnimate ? {
            duration: leaf.duration,
            repeat: Infinity,
            ease: 'linear',
            delay: leaf.delay
          } : {}}
        />
      ))}

      {fireflies.map((firefly) => (
        <motion.div
          key={firefly.id}
          className='absolute rounded-full bg-yellow-300'
          style={{
            left: `${firefly.left}%`,
            top: `${firefly.top}%`,
            width: '3px',
            height: '3px',
            filter: 'blur(0.5px)',
            boxShadow: '0 0 8px rgba(253, 224, 71, 0.8)'
          }}
          initial={{ opacity: 0 }}
          animate={shouldAnimate ? {
            opacity: [0, 1, 0],
            x: [-firefly.moveRange, firefly.moveRange, -firefly.moveRange],
            y: [-firefly.moveRange * 0.5, firefly.moveRange * 0.5, -firefly.moveRange * 0.5],
            scale: [0.5, 1.5, 0.5]
          } : { opacity: 0.5, x: 0, y: 0, scale: 1 }}
          transition={shouldAnimate ? {
            duration: firefly.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: firefly.delay
          } : {}}
        />
      ))}
    </div>
  );
});

JungleEffect.displayName = 'JungleEffect';

type Bubble = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  wobble: number;
};

const OceanEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const bubbles = useMemo(
    () =>
      createSequence(30).map<Bubble>((id) => {
        const left = pseudoRandom(id + 800) * 100;
        const delay = pseudoRandom(id + 801) * 3;
        const duration = 3 + pseudoRandom(id + 802) * 4;
        const size = 4 + pseudoRandom(id + 803) * 8;
        const wobble = 10 + pseudoRandom(id + 804) * 20;
        return { id, left, delay, duration, size, wobble };
      }),
    []
  );

  const seaweed = useMemo(
    () =>
      createSequence(8).map((id) => ({
        id: id + 4000,
        left: pseudoRandom(id + 900) * 100,
        delay: pseudoRandom(id + 901) * 2,
        duration: 4 + pseudoRandom(id + 902) * 3,
        height: 60 + pseudoRandom(id + 903) * 80,
        sway: 15 + pseudoRandom(id + 904) * 25
      })),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      <motion.div
        className='absolute inset-0 bg-gradient-to-b from-blue-600/20 via-cyan-600/15 to-teal-600/10'
        initial={{ opacity: 0.6 }}
        animate={shouldAnimate ? { opacity: [0.5, 0.8, 0.5] } : { opacity: 0.6 }}
        transition={shouldAnimate ? { duration: 6, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      <motion.div
        className='absolute inset-0 bg-gradient-to-t from-blue-500/10 to-transparent'
        initial={{ y: 0 }}
        animate={shouldAnimate ? { y: [-10, 10, -10] } : { y: 0 }}
        transition={shouldAnimate ? { duration: 8, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      {bubbles.map((bubble) => (
        <motion.div
          key={bubble.id}
          className='absolute rounded-full bg-cyan-200/40 border border-cyan-300/30'
          style={{
            left: `${bubble.left}%`,
            width: `${bubble.size}px`,
            height: `${bubble.size}px`,
            filter: 'blur(0.5px)',
            boxShadow: '0 0 6px rgba(165, 243, 252, 0.3), inset 0 0 4px rgba(255, 255, 255, 0.2)'
          }}
          initial={{ y: '100vh', opacity: 0 }}
          animate={shouldAnimate ? {
            y: '-20px',
            x: [0, bubble.wobble, -bubble.wobble, 0],
            opacity: [0, 0.8, 0.8, 0],
            scale: [0.5, 1, 1.2, 0.8]
          } : { y: '60vh', x: 0, opacity: 0.4, scale: 1 }}
          transition={shouldAnimate ? {
            duration: bubble.duration,
            repeat: Infinity,
            ease: 'easeOut',
            delay: bubble.delay
          } : {}}
        />
      ))}

      {seaweed.map((weed) => (
        <motion.div
          key={weed.id}
          className='absolute bottom-0 bg-gradient-to-t from-green-600/60 to-green-400/30'
          style={{
            left: `${weed.left}%`,
            width: '4px',
            height: `${weed.height}px`,
            borderRadius: '2px 2px 0 0',
            filter: 'blur(1px)',
            transformOrigin: 'bottom center'
          }}
          initial={{ rotate: 0 }}
          animate={shouldAnimate ? {
            rotate: [-weed.sway, weed.sway, -weed.sway],
            scaleY: [1, 1.1, 0.9, 1]
          } : { rotate: 0, scaleY: 1 }}
          transition={shouldAnimate ? {
            duration: weed.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: weed.delay
          } : {}}
        />
      ))}
    </div>
  );
});

OceanEffect.displayName = 'OceanEffect';

type BlizzardFlake = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  drift: number;
  intensity: number;
};

const ArcticEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const blizzardFlakes = useMemo(
    () =>
      createSequence(60).map<BlizzardFlake>((id) => {
        const left = pseudoRandom(id + 1000) * 100;
        const delay = pseudoRandom(id + 1001) * 2;
        const duration = 1.5 + pseudoRandom(id + 1002) * 2;
        const size = 1 + pseudoRandom(id + 1003) * 3;
        const drift = pseudoRandom(id + 1004) * 80 - 40;
        const intensity = 0.4 + pseudoRandom(id + 1005) * 0.6;
        return { id, left, delay, duration, size, drift, intensity };
      }),
    []
  );

  const windGusts = useMemo(
    () =>
      createSequence(6).map((id) => ({
        id: id + 5000,
        delay: pseudoRandom(id + 1100) * 8,
        duration: 2 + pseudoRandom(id + 1101) * 3,
        intensity: 0.1 + pseudoRandom(id + 1102) * 0.15
      })),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      <motion.div
        className='absolute inset-0 bg-gradient-to-b from-slate-300/20 via-slate-400/15 to-slate-500/10'
        initial={{ opacity: 0.8 }}
        animate={shouldAnimate ? { opacity: [0.7, 1, 0.7] } : { opacity: 0.8 }}
        transition={shouldAnimate ? { duration: 4, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      {windGusts.map((gust) => (
        <motion.div
          key={gust.id}
          className='absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent'
          style={{ transform: 'skewX(-15deg)' }}
          initial={{ x: '-100%', opacity: 0 }}
          animate={shouldAnimate ? {
            x: '200%',
            opacity: [0, gust.intensity, gust.intensity, 0]
          } : { x: '50%', opacity: gust.intensity * 0.5 }}
          transition={shouldAnimate ? {
            duration: gust.duration,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: gust.delay
          } : {}}
        />
      ))}

      {blizzardFlakes.map((flake) => (
        <motion.div
          key={flake.id}
          className='absolute rounded-full bg-white'
          style={{
            left: `${flake.left}%`,
            width: `${flake.size}px`,
            height: `${flake.size}px`,
            opacity: flake.intensity,
            filter: 'blur(0.8px)',
            boxShadow: '0 0 3px rgba(255, 255, 255, 0.4)'
          }}
          initial={{ y: '-20px', x: 0 }}
          animate={shouldAnimate ? {
            y: 'calc(100vh + 20px)',
            x: [0, flake.drift * 0.3, flake.drift, flake.drift * 1.2, flake.drift * 0.8],
            rotate: [0, 180, 360, 540]
          } : { y: '40vh', x: 0, rotate: 0 }}
          transition={shouldAnimate ? {
            duration: flake.duration,
            repeat: Infinity,
            ease: 'linear',
            delay: flake.delay
          } : {}}
        />
      ))}

      <motion.div
        className='absolute inset-0 bg-gradient-to-br from-slate-200/5 via-transparent to-slate-300/5'
        initial={{ opacity: 0.3 }}
        animate={shouldAnimate ? {
          opacity: [0.2, 0.5, 0.2],
          scale: [1, 1.02, 1]
        } : { opacity: 0.3, scale: 1 }}
        transition={shouldAnimate ? { duration: 6, repeat: Infinity, ease: 'easeInOut' } : {}}
      />
    </div>
  );
});

ArcticEffect.displayName = 'ArcticEffect';

type SmokeParticle = {
  id: number;
  left: number;
  delay: number;
  duration: number;
  size: number;
  drift: number;
  opacity: number;
  blur: number;
};

const HotboxedEffect = memo(({ shouldAnimate }: { shouldAnimate: boolean }) => {
  const getAnimationProps = (animateValues: any, transitionProps: any, staticValues: any) => ({
    animate: shouldAnimate ? animateValues : staticValues,
    transition: shouldAnimate ? transitionProps : {}
  });

  const smokeParticles = useMemo(
    () =>
      createSequence(20).map<SmokeParticle>((id) => {
        const left = pseudoRandom(id + 1200) * 120 - 10;
        const delay = pseudoRandom(id + 1201) * 8;
        const duration = 15 + pseudoRandom(id + 1202) * 10;
        const size = 60 + pseudoRandom(id + 1203) * 80;
        const drift = pseudoRandom(id + 1204) * 40 - 20;
        const opacity = 0.2 + pseudoRandom(id + 1205) * 0.3;
        const blur = 2 + pseudoRandom(id + 1206) * 3;
        return { id, left, delay, duration, size, drift, opacity, blur };
      }),
    []
  );

  const denseClouds = useMemo(
    () =>
      createSequence(8).map((id) => ({
        id: id + 7000,
        left: pseudoRandom(id + 1400) * 140 - 20,
        delay: pseudoRandom(id + 1401) * 12,
        duration: 20 + pseudoRandom(id + 1402) * 15,
        size: 120 + pseudoRandom(id + 1403) * 160,
        opacity: 0.1 + pseudoRandom(id + 1404) * 0.2,
        drift: pseudoRandom(id + 1405) * 30 - 15
      })),
    []
  );

  return (
    <div className='absolute inset-0 pointer-events-none overflow-hidden'>
      <motion.div
        className='absolute inset-0 bg-gradient-to-t from-gray-800/40 via-gray-700/30 to-gray-600/20'
        initial={{ opacity: 0.9 }}
        animate={shouldAnimate ? { opacity: [0.8, 1, 0.8] } : { opacity: 0.9 }}
        transition={shouldAnimate ? { duration: 12, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      <motion.div
        className='absolute inset-0 bg-gradient-to-br from-gray-600/25 via-gray-500/35 to-gray-700/20'
        initial={{ opacity: 0.9 }}
        animate={shouldAnimate ? {
          opacity: [0.8, 1, 0.8],
          scale: [1, 1.005, 1]
        } : { opacity: 0.9, scale: 1 }}
        transition={shouldAnimate ? { duration: 15, repeat: Infinity, ease: 'easeInOut' } : {}}
      />

      {denseClouds.map((cloud) => (
        <motion.div
          key={cloud.id}
          className='absolute rounded-full'
          style={{
            left: `${cloud.left}%`,
            width: `${cloud.size}px`,
            height: `${cloud.size * 0.8}px`,
            opacity: cloud.opacity,
            filter: 'blur(6px)',
            background: `radial-gradient(ellipse, rgba(220, 220, 220, ${cloud.opacity * 1.5}) 0%, rgba(180, 180, 180, ${cloud.opacity}) 30%, rgba(140, 140, 140, ${cloud.opacity * 0.6}) 60%, transparent 100%)`,
            borderRadius: '60% 40% 50% 70%'
          }}
          initial={{ y: '110vh', scale: 0.3, rotate: 0 }}
          {...getAnimationProps(
            {
              y: [
                '110vh',
                '80vh',
                '50vh',
                '20vh',
                '-10vh',
                '-20vh'
              ],
              x: [
                0,
                cloud.drift * 0.1,
                cloud.drift * 0.3 + Math.sin(pseudoRandom(cloud.id + 100) * 6.28) * 15,
                cloud.drift * 0.6 + Math.sin(pseudoRandom(cloud.id + 200) * 6.28) * 20,
                cloud.drift * 0.8 + Math.sin(pseudoRandom(cloud.id + 300) * 6.28) * 10,
                cloud.drift
              ],
              scale: [0.3, 0.6, 0.9, 1.2, 1.4, 1.5],
              rotate: [
                0,
                pseudoRandom(cloud.id + 400) * 10 - 5,
                pseudoRandom(cloud.id + 500) * 20 - 10,
                pseudoRandom(cloud.id + 600) * 30 - 15,
                pseudoRandom(cloud.id + 700) * 40 - 20,
                pseudoRandom(cloud.id + 800) * 50 - 25
              ],
              opacity: [0, cloud.opacity * 0.3, cloud.opacity * 0.7, cloud.opacity, cloud.opacity * 0.6, 0]
            },
            {
              duration: cloud.duration,
              repeat: Infinity,
              ease: [0.25, 0.1, 0.25, 1],
              delay: cloud.delay,
              times: [0, 0.1, 0.3, 0.6, 0.85, 1]
            },
            { y: '50vh', x: 0, scale: 1, rotate: 0, opacity: cloud.opacity }
          )}
        />
      ))}

      {smokeParticles.map((particle) => (
        <motion.div
          key={particle.id}
          className='absolute rounded-full'
          style={{
            left: `${particle.left}%`,
            width: `${particle.size}px`,
            height: `${particle.size}px`,
            opacity: particle.opacity,
            filter: `blur(${particle.blur}px)`,
            background: `radial-gradient(circle, rgba(220, 220, 220, ${particle.opacity}) 0%, rgba(180, 180, 180, ${particle.opacity * 0.8}) 30%, rgba(140, 140, 140, ${particle.opacity * 0.5}) 60%, transparent 100%)`
          }}
          initial={{ y: '100vh', scale: 0.2, rotate: 0 }}
          {...getAnimationProps(
            {
              y: [
                '100vh',
                '75vh',
                '45vh',
                '15vh',
                '-5vh',
                '-20vh'
              ],
              x: [
                0,
                particle.drift * 0.1 + Math.sin(pseudoRandom(particle.id + 100) * 6.28) * 8,
                particle.drift * 0.25 + Math.sin(pseudoRandom(particle.id + 200) * 6.28) * 12,
                particle.drift * 0.5 + Math.sin(pseudoRandom(particle.id + 300) * 6.28) * 18,
                particle.drift * 0.75 + Math.sin(pseudoRandom(particle.id + 400) * 6.28) * 14,
                particle.drift + Math.sin(pseudoRandom(particle.id + 500) * 6.28) * 10
              ],
              scale: [
                0.2,
                0.4 + pseudoRandom(particle.id + 600) * 0.2,
                0.8 + pseudoRandom(particle.id + 700) * 0.3,
                1.1 + pseudoRandom(particle.id + 800) * 0.3,
                1.4 + pseudoRandom(particle.id + 900) * 0.2,
                1.6
              ],
              rotate: [
                0,
                pseudoRandom(particle.id + 1000) * 15 - 7.5,
                pseudoRandom(particle.id + 1100) * 30 - 15,
                pseudoRandom(particle.id + 1200) * 45 - 22.5,
                pseudoRandom(particle.id + 1300) * 60 - 30,
                pseudoRandom(particle.id + 1400) * 80 - 40
              ],
              opacity: [0, particle.opacity * 0.4, particle.opacity * 0.8, particle.opacity, particle.opacity * 0.7, 0]
            },
            {
              duration: particle.duration,
              repeat: Infinity,
              ease: [0.25, 0.1, 0.25, 1],
              delay: particle.delay,
              times: [0, 0.15, 0.35, 0.65, 0.85, 1]
            },
            { y: '45vh', x: 0, scale: 1, rotate: 0, opacity: particle.opacity }
          )}
        />
      ))}

      <motion.div
        className='absolute bottom-0 left-0 right-0 h-60 bg-gradient-to-t from-gray-600/30 via-gray-500/20 to-transparent'
        initial={{ opacity: 0.8 }}
        {...getAnimationProps(
          {
            opacity: [0.7, 0.9, 0.7],
            y: [0, -3, 0]
          },
          { duration: 18, repeat: Infinity, ease: 'easeInOut' },
          { opacity: 0.8, y: 0 }
        )}
      />
    </div>
  );
});

HotboxedEffect.displayName = 'HotboxedEffect';

type WeatherEffectsProps = {
  variant?: EnvironmentVariant | null;
  shouldAnimate?: boolean;
};

const StaticGradient = memo(({ variant }: { variant: EnvironmentVariant }) => {
  const gradientClass = useMemo(() => {
    if (variant === 'snow') return 'bg-gradient-to-b from-sky-200/20 via-sky-100/15 to-sky-50/10';
    if (variant === 'rain') return 'bg-gradient-to-b from-blue-600/25 via-blue-500/15 to-blue-400/10';
    if (variant === 'sunny') return 'bg-gradient-to-b from-yellow-400/30 via-amber-300/20 to-orange-300/15';
    if (variant === 'space') return 'bg-gradient-to-b from-purple-900/30 via-indigo-800/20 to-black/15';
    if (variant === 'jungle') return 'bg-gradient-to-b from-green-700/25 via-emerald-600/15 to-green-500/10';
    if (variant === 'ocean') return 'bg-gradient-to-b from-blue-600/25 via-cyan-500/15 to-teal-400/10';
    if (variant === 'arctic') return 'bg-gradient-to-b from-slate-300/25 via-slate-200/15 to-slate-100/10';
    if (variant === 'hotboxed') return 'bg-gradient-to-b from-gray-700/40 via-gray-600/25 to-gray-500/15';
    return 'bg-gradient-to-b from-gray-500/10 via-gray-400/5 to-transparent';
  }, [variant]);

  return <div className={`absolute inset-0 pointer-events-none ${gradientClass}`} />;
});

StaticGradient.displayName = 'StaticGradient';

const WeatherEffects = memo(({ variant, shouldAnimate = true }: WeatherEffectsProps) => {
  if (!variant) {
    return null;
  }

  if (!shouldAnimate) {
    return <StaticGradient variant={variant} />;
  }

  const effectProps = { shouldAnimate };

  if (variant === 'snow') {
    return <SnowEffect {...effectProps} />;
  }

  if (variant === 'rain') {
    return <RainEffect {...effectProps} />;
  }

  if (variant === 'sunny') {
    return <SunnyEffect {...effectProps} />;
  }

  if (variant === 'space') {
    return <SpaceEffect {...effectProps} />;
  }

  if (variant === 'jungle') {
    return <JungleEffect {...effectProps} />;
  }

  if (variant === 'ocean') {
    return <OceanEffect {...effectProps} />;
  }

  if (variant === 'arctic') {
    return <ArcticEffect {...effectProps} />;
  }

  if (variant === 'hotboxed') {
    return <HotboxedEffect {...effectProps} />;
  }

  return null;
});

WeatherEffects.displayName = 'WeatherEffects';

export type { EnvironmentVariant };
export default WeatherEffects;

